import React, { useState, useEffect } from 'react'
import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import ListItemText from '@mui/material/ListItemText'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Checkbox from '@mui/material/Checkbox'
import { FormHelperText } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 150,
    },
  },
}

interface MultiSelectCheckmarksProps {
  dataTestId?: string
  label: string
  items: string[]
  selectedItems: string[]
  onItemChange: (selectedItems: string[]) => void
  onDeselectItem?: (item: string, confirmDeselect: () => void, cancelDeselect: () => void) => void
  error?: boolean
  disabled?: boolean
  helperText?: string
}

export const MultiSelectCheckMarks: React.FC<MultiSelectCheckmarksProps> = ({
  dataTestId = 'multi-select-checkmarks',
  label,
  items,
  selectedItems,
  onItemChange,
  onDeselectItem,
  helperText,
  disabled = false,
  error,
}) => {
  const [selected, setSelected] = useState<string[]>(selectedItems)

  useEffect(() => {
    setSelected(selectedItems)
  }, [selectedItems])

  const handleChange = (event: SelectChangeEvent<typeof selected>) => {
    const {
      target: { value },
    } = event
    const newSelected = typeof value === 'string' ? value.split(',') : value

    // Check if an item is being deselected
    const deselectedItem = selected.find((item) => !newSelected.includes(item))
    if (deselectedItem && onDeselectItem) {
      onDeselectItem(
        deselectedItem,
        () => {
          setSelected(newSelected)
          onItemChange(newSelected)
        },
        () => {
          // Cancel deselect, do nothing
        },
      )
    } else {
      setSelected(newSelected)
      onItemChange(newSelected)
    }
  }

  return (
    <FormControl
      sx={{ mr: 1, width: '100%' }}
      error={error}
      data-testid={`${dataTestId}-form-control`}
    >
      <Select
        labelId={`${dataTestId}-label`}
        id={`${dataTestId}-select`}
        disabled={disabled}
        multiple
        displayEmpty // Add this prop to show the placeholder when no items are selected
        value={selected}
        onChange={handleChange}
        input={<OutlinedInput sx={{ padding: '8px 14px', height: '56px' }} />}
        renderValue={(selected) => {
          if (selected.length === 0) {
            return <span style={{ color: 'var(--base600)' }}>{label}</span> // Show placeholder when no items are selected
          }
          return selected.join(', ')
        }}
        MenuProps={MenuProps}
        IconComponent={KeyboardArrowDownIcon}
        sx={{ height: '48px', display: 'flex', alignItems: 'center' }}
        data-testid={`${dataTestId}-select`}
      >
        {items.map((item) => (
          <MenuItem
            key={item}
            value={item}
            data-testid={`${dataTestId}-menu-item-${item}`}
          >
            <Checkbox
              checked={selected.includes(item)}
              data-testid={`${dataTestId}-checkbox-${item}`}
            />
            <ListItemText primary={item} />
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText data-testid={`${dataTestId}-helper-text`}>{helperText}</FormHelperText>}
    </FormControl>
  )
}
